import { ref } from 'vue'
import { useApi } from './useApi.js'

// Dashboard tab badges (Attendance / Invoices / Signups) — counts of what each tab
// asks the teacher to do, from /api/lessons/badges. Polled, refetched when the
// window regains focus, and right after any write that could change them (useApi's
// fetchApi fires 'teacher-badges-stale'). Demo Mode's preview-as-teacher applies via
// the fetch interceptor (/api/lessons/ prefix), so callers refresh on preview change.
const POLL_MS = 2 * 60 * 1000

const badges = ref({ attendance: 0, invoices: 0, signups: 0 })
let pollTimer = null
let refCount = 0
let staleTimer = null

async function refresh() {
  try {
    badges.value = await useApi().fetchApi('/api/lessons/badges')
  } catch {
    // Keep the last known counts rather than flashing the badges to zero.
  }
}

// Several writes often land together (a group lesson's attendance, one per student).
function onStale() {
  clearTimeout(staleTimer)
  staleTimer = setTimeout(refresh, 500)
}
const onVisible = () => { if (document.visibilityState === 'visible') refresh() }

export function formatBadge(n) {
  return n > 99 ? '99+' : String(n)
}

export function useTeacherBadges() {
  const start = () => {
    refCount += 1
    if (pollTimer) return
    refresh()
    pollTimer = setInterval(refresh, POLL_MS)
    window.addEventListener('teacher-badges-stale', onStale)
    document.addEventListener('visibilitychange', onVisible)
  }

  const stop = () => {
    refCount = Math.max(0, refCount - 1)
    if (refCount === 0 && pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
      window.removeEventListener('teacher-badges-stale', onStale)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }

  return { badges, refresh, start, stop }
}
